/** Money and number formatting of CRM amounts (amounts arrive as JSON numbers in a currency). */

/**
 * How a currency is written: the hryvnia always as "₴" (English would write "UAH 1,250"), other currencies with their
 * usual symbol in the UI language ("$", "€").
 */
function display(currency: string): Intl.NumberFormatOptions['currencyDisplay'] {
  return currency === 'UAH' ? 'narrowSymbol' : 'symbol';
}

/** "1 250 000 ₴" / "₴40,000" / "$40,000" in the UI language; whole units unless the amount has kopecks. */
export function formatMoney(amount: number | null | undefined, currency: string, locale: string): string {
  if (amount === null || amount === undefined) return '—';
  const fraction = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: display(currency),
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${currency}`;
  }
}

/** "1,2 млн ₴" — compact sums for board columns. */
export function formatCompactMoney(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: display(currency),
      notation: amount >= 100_000 ? 'compact' : 'standard',
      maximumFractionDigits: amount >= 100_000 ? 1 : 0,
    }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${currency}`;
  }
}

/** The symbol or code of a currency in the UI language ("₴", "$", "€"). */
export function currencySymbol(currency: string, locale: string): string {
  try {
    const part = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? currency;
  } catch {
    return currency;
  }
}

/** Parses "1 250 000,50" / "1,250,000.50" into a number; null for empty or invalid input. */
export function parseAmount(text: string): number | null {
  const cleaned = text.replace(/[\s\u00a0\u202f]/gu, '');
  if (cleaned === '') return null;
  // Groups of three digits after separators are thousands ("12,345", "1.234.567"): amounts have at most two decimals.
  if (/^\d{1,3}([.,]\d{3})+$/u.test(cleaned)) return Number(cleaned.replace(/[.,]/gu, ''));
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;
  if (lastComma > lastDot) normalized = cleaned.replace(/\./gu, '').replace(',', '.');
  else normalized = cleaned.replace(/,/gu, '');
  if (!/^\d+(\.\d{1,2})?$/u.test(normalized)) return null;
  return Number(normalized);
}

/** Amount in the base currency with the manual rates. */
export function toBase(amount: number, currency: string, rates: { code: string; rate: number }[]): number {
  return amount * (rates.find((r) => r.code === currency)?.rate ?? 1);
}

/**
 * The manual rate between two currencies, written the readable way round: "1 $ = 41,5 ₴" (never "1 ₴ = 0,02 $").
 */
export function exchangeRateText(
  from: string,
  to: string,
  currencies: readonly { code: string; rate: number }[],
  format: (amount: number, currency: string) => string,
): string {
  const rate = (code: string) => currencies.find((c) => c.code === code)?.rate ?? 1;
  const ratio = rate(from) / rate(to);
  const round = (value: number) => Math.round(value * 10_000) / 10_000;
  return ratio >= 1
    ? `${format(1, from)} = ${format(round(ratio), to)}`
    : `${format(1, to)} = ${format(round(1 / ratio), from)}`;
}
